/* eslint-disable prettier/prettier */

import * as React from "react";
import moment from "moment";
import {
  Grid,
  Link,
  TextField,
  Button,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
  Box,
  Stack,
  OutlinedInput,
  InputLabel,
} from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { AuthenticationContext, ImagesContext } from "../../../Context";
import { getHeaders } from "../../../utils/HeaderHelper";
import { fetchDiagnosisOptions } from "../../../utils/DiagnosisSearch";
import { useFlash } from "../../../FlashContext";
import { PrivilegesContext } from "../../../PrivilegesContext";
import { checkPrivileges } from "../../../utils/PrivilegesHelper";
import type { DiagnosisOption } from "../../../utils/DiagnosisSearch";

// key=6 is the "Care Gap" activity group in intervention_dropdown_lookups,
// containing: Asthma, Diabetes, Other Condition.
// Care Gap Closure intentionally shares this group with Care Gap Evaluation
// rather than having its own separate options.
//
// This value is safe across every environment: it's a literal Ruby hash key
// (not an auto-incrementing DB id) defined in
// InterventionDropdownLookupSeeder::DEFAULT_LOOKUP_VALUES, and every
// organization is seeded from that same hash, so key 6 always means "Care
// Gap" everywhere.
const CARE_GAP_ACTIVITY_KEY = 6;

const INTERVENTION_TYPE_TO_ACTIVITY_KEY: Record<string, number> = {
  "Add Therapy": 2,
  Adherence: 3,
  "Adjust Therapy": 4,
  "Care Gap Closure": CARE_GAP_ACTIVITY_KEY,
  "Care Gap Evaluation": CARE_GAP_ACTIVITY_KEY,
  "Cost Savings": 7,
  "Discontinue Therapy": 8,
  Education: 9,
};

const VACCINATION_TYPE = "Vaccination";
const VACCINATION_INTERNAL_DX_CODE = "Z23";
const VACCINATION_INTERNAL_DX_DESC = "Encounter for immunization";
const VACCINATION_ACTIVITY_OPTIONS = [
  "Up to date--no vax needed",
  "Not up to date",
  "Vaccination administered",
];
const VACCINE_OPTIONS = [
  "COVID",
  "Hepatitis B",
  "HPV",
  "Influenza",
  "Meningococcal",
  "Pneumonia",
  "RSV",
  "Shingles",
  "TDAP",
  "Other",
];

const PRESCRIBER_RESPONSE_OPTIONS = [
  "Recommendation Accepted",
  "Recommendation Declined",
  "No Response",
];

const formatSavingsToTwoDecimals = (
  value: string | number | null | undefined,
): string => {
  if (value == null) return "";
  const normalized = String(value).trim();
  if (normalized === "") return "";
  const numeric = Number(normalized);
  if (Number.isNaN(numeric)) return normalized;
  return numeric.toFixed(2);
};

// Matches Login Page Content Management: TextField/Select inputs use `the-field`
const selectInput = <OutlinedInput className="the-field" />;

function WorkflowCheckboxRow(props: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
  title: string;
  description: string;
}) {
  const { checked, onChange, disabled, title, description } = props;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2.5 }}>
      <Checkbox
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{ p: 0.25, mt: "2px" }}
        color="primary"
      />
      <Box sx={{ minWidth: 0 }}>
        <InputLabel
          className="field-label"
          sx={{
            position: "static",
            transform: "none",
            margin: 0,
            padding: 0,
            color: "#A29D9B",
            fontWeight: 700,
            fontSize: "12px",
            lineHeight: "18px",
          }}
        >
          {title}
        </InputLabel>
        <Box
          sx={{
            color: "#868382",
            fontFamily: "QuicksandMedium",
            fontSize: "13px",
            fontWeight: 500,
            lineHeight: "20px",
            mt: "6px",
          }}
        >
          {description}
        </Box>
      </Box>
    </Box>
  );
}

interface Props {}

const InterventionInformationScreen: React.FC<Props> = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const userPrivileges = React.useContext(PrivilegesContext);
  const images = React.useContext(ImagesContext);
  const { showToast } = useFlash();
  const history = useHistory();
  const { id: patientId } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const interventionId = queryParams.get("intervention_id");
  const viewOnly = queryParams.get("view_only") === "1";
  const linkedEncounterUuidFromSource = queryParams.get("encounter_id") || "";

  const [interventionTypes, setInterventionTypes] = React.useState<
    { key: number; value: string }[]
  >([]);
  const [activityOptions, setActivityOptions] = React.useState<
    { key: number; value: string }[]
  >([]);
  const [dateOfIntervention, setDateOfIntervention] = React.useState<string>(
    moment().format("YYYY-MM-DD"),
  );
  const [interventionType, setInterventionType] = React.useState<string>("");
  const [activity, setActivity] = React.useState<string>("");
  const [vaccineName, setVaccineName] = React.useState<string>("");
  const [relatedConditionDx, setRelatedConditionDx] =
    React.useState<DiagnosisOption | null>(null);
  /** Patient profile + encounter history (same sources as General Info Diagnosis Tag Manager). */
  const [patientDxOptions, setPatientDxOptions] = React.useState<
    DiagnosisOption[]
  >([]);
  const [nlmDxOptions, setNlmDxOptions] = React.useState<DiagnosisOption[]>([]);
  const nlmDxDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [relatedDxInputValue, setRelatedDxInputValue] =
    React.useState<string>("");
  const [medicationInvolved, setMedicationInvolved] =
    React.useState<string>("");
  const [medicationSearch, setMedicationSearch] = React.useState<any>(null);
  const [deviceInvolved, setDeviceInvolved] = React.useState<string>("");
  const [notes, setNotes] = React.useState<string>("");
  const [encounterBillingUuid, setEncounterBillingUuid] =
    React.useState<string>("");
  const [needsSupervisorReview, setNeedsSupervisorReview] =
    React.useState(false);
  const [receivedPrescriberResponse, setReceivedPrescriberResponse] =
    React.useState(false);
  const [prescriberResponseValue, setPrescriberResponseValue] =
    React.useState<string>("");
  const [annualizedHardSavings, setAnnualizedHardSavings] =
    React.useState(false);
  const [savingsAmount, setSavingsAmount] = React.useState<string>("");
  const [disableSave, setDisableSave] = React.useState(false);
  const [interventionStatus, setInterventionStatus] =
    React.useState<string>("pended");
  const [openInterventionDate, setOpenInterventionDate] =
    React.useState<boolean>(false);
  const prevInterventionTypeRef = React.useRef<string>("");
  const canSignIntervention = !!checkPrivileges(
    userPrivileges,
    "Sign Intervention Note",
  );

  const isVaccinationType = interventionType === VACCINATION_TYPE;
  // Signed interventions are immutable (enforced server-side too — see
  // InterventionsController#update). Disabling client-side as well avoids a
  // confusing "why won't this save" round trip if a signed record is ever
  // opened without view_only=1 (e.g. a stale bookmark or a typed-in URL).
  const isLockedForEditing = viewOnly || interventionStatus === "signed";
  const interventionDateValue = dateOfIntervention
    ? moment(dateOfIntervention)
    : null;

  const loadInterventionTypes = React.useCallback(() => {
    fetch("/intervention_dropdown_lookups?key=1", {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success && result.resource) {
          const types = Array.isArray(result.resource) ? result.resource : [];
          const hasVaccination = types.some(
            (t: { value: string }) => t.value === VACCINATION_TYPE,
          );
          setInterventionTypes(
            hasVaccination
              ? types
              : [...types, { key: -1, value: VACCINATION_TYPE }],
          );
        }
      })
      .catch(() => {});
  }, [authenticationSetting.csrfToken]);

  // Guards against out-of-order fetch responses: if the user changes
  // interventionType again before an in-flight activity-options request
  // resolves, that request's result must not be allowed to land — it would
  // silently replace the correct dropdown with options for a type the user
  // has already navigated away from. Bumped once per effect run below.
  const activityOptionsGenerationRef = React.useRef(0);

  const loadActivityOptions = React.useCallback(
    (key: number, generation: number) => {
      fetch(`/intervention_dropdown_lookups?key=${key}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((res) => res.json())
        .then((result) => {
          if (generation !== activityOptionsGenerationRef.current) return;
          if (result.success && result.resource) {
            setActivityOptions(result.resource);
          } else {
            setActivityOptions([]);
          }
        })
        .catch(() => {
          if (generation !== activityOptionsGenerationRef.current) return;
          setActivityOptions([]);
        });
    },
    [authenticationSetting.csrfToken],
  );

  React.useEffect(() => {
    loadInterventionTypes();
  }, [loadInterventionTypes]);

  React.useEffect(() => {
    activityOptionsGenerationRef.current += 1;
    const generation = activityOptionsGenerationRef.current;
    if (!interventionType) {
      setActivityOptions([]);
      setActivity("");
      prevInterventionTypeRef.current = "";
      return;
    }
    if (interventionType === VACCINATION_TYPE) {
      setActivityOptions(
        VACCINATION_ACTIVITY_OPTIONS.map((value, index) => ({
          key: index + 1,
          value,
        })),
      );
      if (
        prevInterventionTypeRef.current !== "" &&
        prevInterventionTypeRef.current !== interventionType
      ) {
        setActivity("");
      }
      setRelatedConditionDx(null);
      setRelatedDxInputValue("");
      prevInterventionTypeRef.current = interventionType;
      return;
    }
    setVaccineName("");
    const key = INTERVENTION_TYPE_TO_ACTIVITY_KEY[interventionType];
    if (key) {
      loadActivityOptions(key, generation);
      if (
        prevInterventionTypeRef.current !== "" &&
        prevInterventionTypeRef.current !== interventionType
      ) {
        setActivity("");
      }
      prevInterventionTypeRef.current = interventionType;
    } else {
      setActivityOptions([]);
      setActivity("");
      prevInterventionTypeRef.current = interventionType;
    }
  }, [interventionType, loadActivityOptions]);

  React.useEffect(() => {
    if (!interventionId || !patientId) return;
    fetch(`/interventions/${interventionId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success && result.resource) {
          const r = result.resource;
          setDateOfIntervention(r.occurred_on_date || "");
          setInterventionType(r.intervention_type || "");
          setActivity(r.activity || "");
          setVaccineName(r.vaccine_name || "");
          if (r.related_condition_code || r.related_condition_description) {
            const dx = [
              r.related_condition_code,
              r.related_condition_description,
            ].filter(Boolean) as DiagnosisOption;
            setRelatedConditionDx(dx);
            setRelatedDxInputValue(`${dx[0]} — ${dx[1] ?? ""}`);
          } else {
            setRelatedConditionDx(null);
            setRelatedDxInputValue("");
          }
          setMedicationInvolved(r.related_medication || "");
          setDeviceInvolved(r.related_device || "");
          setNotes(r.notes || "");
          setEncounterBillingUuid(r.encounter_billing_uuid || "");
          setNeedsSupervisorReview(!!r.is_ready_for_review);
          setReceivedPrescriberResponse(!!r.has_prescriber_response);
          setPrescriberResponseValue(r.has_prescriber_response || "");
          setAnnualizedHardSavings(!!r.has_savings);
          setSavingsAmount(formatSavingsToTwoDecimals(r.saving_amounts));
          setInterventionStatus(r.status || "pended");
        }
      })
      .catch(() => {});
  }, [interventionId, patientId, authenticationSetting.csrfToken]);

  React.useEffect(() => {
    if (viewOnly) return;
    if (medicationInvolved) {
      fetch(`/medication_search`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          medication: {
            name: medicationInvolved,
          },
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (typeof result.error !== "undefined") {
            setMedicationSearch(null);
          } else {
            setMedicationSearch(result.data.results);
          }
        })
        .catch(() => {
          setMedicationSearch(null);
        });
    } else {
      setMedicationSearch(null);
    }
  }, [medicationInvolved, viewOnly, authenticationSetting.csrfToken]);

  React.useEffect(() => {
    if (!patientId) return;
    const headers = getHeaders(authenticationSetting.csrfToken);
    Promise.all([
      fetch(`/data_fetching/diagnoses/assigned?patient_id=${patientId}`, {
        method: "GET",
        headers,
      }).then((r) => r.json()),
      fetch(`/encounters/previously_used_codes/${patientId}`, {
        method: "GET",
        headers,
      }).then((r) => r.json()),
    ])
      .then(([assignedRes, prevRes]) => {
        const byCode = new Map<string, DiagnosisOption>();
        if (assignedRes.success && Array.isArray(assignedRes.resource)) {
          assignedRes.resource.forEach(
            (d: {
              diagnosis_code_value?: string;
              diagnosis_code_desc?: string;
            }) => {
              const c = d.diagnosis_code_value?.trim();
              if (c) byCode.set(c, [c, (d.diagnosis_code_desc || "").trim()]);
            },
          );
        }
        if (prevRes.success && Array.isArray(prevRes.resource?.diag_codes)) {
          prevRes.resource.diag_codes.forEach((pair: [string, string]) => {
            const c = pair?.[0]?.trim();
            if (c && !byCode.has(c)) byCode.set(c, [c, (pair[1] || "").trim()]);
          });
        }
        setPatientDxOptions(
          Array.from(byCode.values()).sort((a, b) =>
            a[0].localeCompare(b[0], undefined, { sensitivity: "base" }),
          ),
        );
      })
      .catch(() => setPatientDxOptions([]));
  }, [patientId, authenticationSetting.csrfToken]);

  React.useEffect(
    () => () => {
      if (nlmDxDebounceRef.current) clearTimeout(nlmDxDebounceRef.current);
    },
    [],
  );

  const fetchNlmDiagnosisForInput = React.useCallback(
    (term: string) => {
      if (nlmDxDebounceRef.current) clearTimeout(nlmDxDebounceRef.current);
      const t = term.trim();
      if (t.length < 2) {
        setNlmDxOptions([]);
        return;
      }
      nlmDxDebounceRef.current = setTimeout(async () => {
        try {
          const options = await fetchDiagnosisOptions(
            t,
            authenticationSetting.csrfToken,
          );
          setNlmDxOptions(options);
        } catch {
          setNlmDxOptions([]);
        }
      }, 320);
    },
    [authenticationSetting.csrfToken],
  );

  React.useEffect(() => {
    if (relatedConditionDx) {
      setRelatedDxInputValue(
        `${relatedConditionDx[0]} — ${relatedConditionDx[1] ?? ""}`,
      );
    }
  }, [relatedConditionDx]);

  const displayDxOptions = React.useMemo(() => {
    const term = relatedDxInputValue.trim().toLowerCase();
    if (term.length === 0) return [];
    const map = new Map<string, DiagnosisOption>();
    patientDxOptions.forEach((o) => {
      const code = (o[0] || "").toLowerCase();
      const desc = (o[1] || "").toLowerCase();
      if (code.includes(term) || desc.includes(term)) map.set(o[0], o);
    });
    nlmDxOptions.forEach((o) => {
      if (o[0] && !map.has(o[0])) map.set(o[0], o);
    });
    return Array.from(map.values());
  }, [patientDxOptions, nlmDxOptions, relatedDxInputValue]);

  const validateFields = (): boolean => {
    if (!dateOfIntervention?.trim()) {
      showToast({
        title: "Validation",
        text: "Date of Intervention is required.",
        type: "error",
      });
      return false;
    }
    if (!interventionType?.trim()) {
      showToast({
        title: "Validation",
        text: "Intervention Type is required.",
        type: "error",
      });
      return false;
    }
    if (!activity?.trim()) {
      showToast({
        title: "Validation",
        text: "Activity is required.",
        type: "error",
      });
      return false;
    }
    if (isVaccinationType && !vaccineName.trim()) {
      showToast({
        title: "Validation",
        text: "Vaccine is required for Vaccination interventions.",
        type: "error",
      });
      return false;
    }
    if (
      annualizedHardSavings &&
      savingsAmount !== "" &&
      !/^\d*(\.\d{0,2})?$/.test(savingsAmount)
    ) {
      showToast({
        title: "Validation",
        text: "Savings amount must be a valid number (e.g. 0.00).",
        type: "error",
      });
      return false;
    }
    return true;
  };

  const getRelatedConditionCode = () =>
    isVaccinationType
      ? VACCINATION_INTERNAL_DX_CODE
      : relatedConditionDx
        ? relatedConditionDx[0]
        : "";
  const getRelatedConditionDescription = () =>
    isVaccinationType
      ? VACCINATION_INTERNAL_DX_DESC
      : relatedConditionDx
        ? relatedConditionDx[1]
        : "";

  const submitForm = (status: "pended" | "signed") => {
    if (!validateFields()) return;
    setDisableSave(true);
    const normalizedSavingsAmount =
      annualizedHardSavings && savingsAmount !== ""
        ? formatSavingsToTwoDecimals(savingsAmount)
        : "";
    if (annualizedHardSavings && savingsAmount !== "") {
      setSavingsAmount(normalizedSavingsAmount);
    }
    const relatedCode = getRelatedConditionCode();
    const relatedDesc = getRelatedConditionDescription();
    const payload = {
      patient_id: patientId,
      status,
      intervention: {
        occurred_on_date: dateOfIntervention,
        intervention_type: interventionType,
        activity: activity.trim(),
        vaccine_name: isVaccinationType ? vaccineName.trim() : null,
        related_condition_code:
          relatedCode === "" ? null : relatedCode,
        related_condition_description:
          relatedDesc === "" ? null : relatedDesc,
        related_medication: medicationInvolved.trim() || null,
        related_device: deviceInvolved.trim() || null,
        notes: notes.trim() || null,
        encounter_billing_uuid: encounterBillingUuid || linkedEncounterUuidFromSource || null,
        is_ready_for_review: needsSupervisorReview,
        has_prescriber_response:
          receivedPrescriberResponse && prescriberResponseValue
            ? prescriberResponseValue
            : null,
        has_savings: annualizedHardSavings,
        saving_amounts:
          annualizedHardSavings && normalizedSavingsAmount !== ""
            ? Number(normalizedSavingsAmount)
            : null,
      },
    };

    const url = interventionId
      ? `/interventions/${interventionId}`
      : "/interventions";
    const method = interventionId ? "PATCH" : "POST";

    fetch(url, {
      method,
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          showToast({
            title: "Intervention Saved",
            text:
              result.message ||
              `Intervention ${status === "signed" ? "signed" : "pended"} successfully.`,
            type: "success",
          });
          if (!interventionId) {
            const id = result.resource?.id;
            if (id)
              history.replace(
                `/patient_reports/${patientId}/interventions/new?intervention_id=${id}`,
              );
          }
          setInterventionStatus(status);
        } else {
          showToast({
            title: "Save Error",
            text:
              result.error || result.message || "Failed to save intervention.",
            type: "error",
          });
        }
        setDisableSave(false);
      })
      .catch((err) => {
        showToast({
          title: "Save Error",
          text: err.message || "Failed to save intervention.",
          type: "error",
        });
        setDisableSave(false);
      });
  };

  const handleBackToList = () => {
    history.push(`/patient_reports/${patientId}/interventions`);
  };

  const relatedEncounterUuid = encounterBillingUuid || linkedEncounterUuidFromSource;
  const relatedEncounterNode = relatedEncounterUuid ? (
    <Link
      href={`/patient_reports/${patientId}/patient_encounters/new?encounter_id=${relatedEncounterUuid}&view_only=1`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        textDecoration: "underline",
        color: "#4A4442",
        fontFamily: "QuicksandMedium",
        fontSize: "13px",
        marginTop: "6px",
        display: "inline-block",
      }}
    >
      View Related Encounter
    </Link>
  ) : null;

  const handleSavingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === "" || /^\d*(\.\d{0,2})?$/.test(v)) setSavingsAmount(v);
  };

  const handleSavingsBlur = () => {
    setSavingsAmount((current) => formatSavingsToTwoDecimals(current));
  };

  const emptySelectPlaceholder = (text: string) => (
    <span
      style={{
        color: "#4A4442",
        fontFamily: "QuicksandMedium",
        fontSize: "14px",
        fontWeight: 500,
      }}
    >
      {text}
    </span>
  );

  const placeholderSx = {
    "& .MuiInputBase-input::placeholder": {
      color: "#4A4442",
      fontWeight: 500,
      opacity: 1,
    },
  };

  const fieldLabelSx = {
    position: "static" as const,
    transform: "none",
    margin: 0,
    padding: 0,
    color: "#A29D9B",
    fontWeight: 700,
    fontSize: "12px",
    lineHeight: "18px",
  };

  const smallOrangeBtnSx = {
    height: "34px",
    padding: "0 12px",
    minWidth: "92px",
    marginLeft: "10px",
  };

  return (
    <Grid container className="panel-container">
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid container direction="row" className="admin-header">
              <Grid item xs={12} className="box-header">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  paddingX={3}
                  paddingY={3}
                  minHeight={4}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Box>
                    <h3 style={{ margin: 0 }}>Intervention Information</h3>
                    {relatedEncounterNode}
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    spacing={0}
                  >
                    <Link
                      component="button"
                      onClick={handleBackToList}
                      sx={{
                        textDecoration: "underline",
                        color: "#4A4442",
                        fontFamily: "QuicksandMedium",
                        fontSize: "14px",
                        marginRight: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Back to Intervention List
                    </Link>
                    {!viewOnly && interventionStatus !== "signed" && (
                      <>
                        {disableSave ? (
                          <>
                            <Button
                              className="orange-btn"
                              disabled
                              sx={smallOrangeBtnSx}
                            >
                              Saving...
                            </Button>
                            {canSignIntervention && (
                              <Button
                                className="orange-btn"
                                disabled
                                sx={smallOrangeBtnSx}
                              >
                                Saving...
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              className="orange-btn"
                              onClick={() => submitForm("pended")}
                              sx={smallOrangeBtnSx}
                            >
                              Pend
                            </Button>
                            {canSignIntervention && (
                              <Button
                                className="orange-btn"
                                onClick={() => submitForm("signed")}
                                sx={smallOrangeBtnSx}
                              >
                                Sign
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            <Grid
              container
              className="grey-container"
              style={{ marginTop: "-11px" }}
            >
              <Grid container className="tabs-container">
                <Grid
                  item
                  xs={12}
                  className="panel-body tab-body-container intervention-information-white-panel"
                  padding={0}
                  mt={1}
                  sx={{
                    border: "0px !important",
                    backgroundColor: "#FFFFFF",
                    padding: { xs: "16px", md: "20px 38px" },
                  }}
                >
                  <Box
                    className="intervention-information-form-content general-patient-info-container"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      maxWidth: "100%",
                      margin: "0 auto",
                    }}
                  >
                    <Box
                      className="info-container"
                      sx={{
                        backgroundColor: "#FFFFFF",
                        padding: "0 !important",
                        boxShadow: "none",
                      }}
                    >
                      <Box
                        className="patient-edit-container"
                        sx={{
                          backgroundColor: "transparent",
                          maxHeight: "100% !important",
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", lg: "row" },
                              gap: 0,
                              width: "100%",
                              alignItems: "stretch",
                            }}
                          >
                            {/* Left column */}
                            <Box
                              sx={{
                                flex: "0 0 305px",
                                maxWidth: "305px",
                                minWidth: 0,
                                pr: { lg: 3 },
                                borderRight: { lg: "1px solid #D9D9D9" },
                                "@media (max-width: 1200px)": {
                                  flex: "1 1 100%",
                                  maxWidth: "100%",
                                  pr: 0,
                                  borderRight: "none",
                                  borderBottom: "1px solid #D9D9D9",
                                  pb: 3,
                                  mb: 2,
                                },
                              }}
                            >
                              <Grid container className="form-container">
                                <Grid
                                  item
                                  xs={12}
                                  className="patient-info-left-container form-left-container"
                                  style={{ marginLeft: 0 }}
                                >
                                  <Grid container>
                                    <Grid item xs={12}>
                                      <InputLabel
                                        className="field-label"
                                        sx={fieldLabelSx}
                                      >
                                        Date of Intervention*
                                      </InputLabel>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      className="field-container"
                                      sx={{ maxWidth: "95% !important" }}
                                    >
                                      <Box
                                        sx={{
                                          marginLeft: "0px",
                                          backgroundColor: "#efe9e7",
                                          display: "flex",
                                          borderRadius: "5px",
                                        }}
                                      >
                                        <LocalizationProvider
                                          dateAdapter={AdapterMoment}
                                        >
                                          <Box
                                            className="date-box"
                                            sx={{
                                              width: "88%",
                                              maxHeight: 41.13,
                                            }}
                                          >
                                            <DemoContainer
                                              components={[
                                                "DatePicker",
                                                "DatePicker",
                                              ]}
                                            >
                                              <DatePicker
                                                open={openInterventionDate}
                                                onOpen={() =>
                                                  setOpenInterventionDate(true)
                                                }
                                                onClose={() =>
                                                  setOpenInterventionDate(false)
                                                }
                                                value={interventionDateValue}
                                                onChange={(x) => {
                                                  setDateOfIntervention(
                                                    x
                                                      ? x.format("YYYY-MM-DD")
                                                      : "",
                                                  );
                                                }}
                                                format="MM/DD/YYYY"
                                                disableOpenPicker
                                                disabled={isLockedForEditing}
                                                slotProps={{
                                                  textField: {
                                                    placeholder: "Enter Date",
                                                  },
                                                  openPickerIcon:
                                                    images.calendar_icon,
                                                }}
                                                className="report-date-input no-border-outline"
                                                maxDate={null}
                                              />
                                            </DemoContainer>
                                          </Box>
                                        </LocalizationProvider>
                                        <Link
                                          className="invite-app-user-link"
                                          onClick={() =>
                                            !isLockedForEditing &&
                                            setOpenInterventionDate(true)
                                          }
                                          sx={{
                                            pointerEvents: isLockedForEditing
                                              ? "none"
                                              : "auto",
                                            opacity: isLockedForEditing
                                              ? 0.45
                                              : 1,
                                          }}
                                        >
                                          <img
                                            src={images.calendar_icon}
                                            alt="Calendar"
                                            className="action-icon-image"
                                            style={{
                                              width: 22.75,
                                              maxHeight: 26,
                                            }}
                                          />
                                        </Link>
                                      </Box>
                                    </Grid>
                                  </Grid>

                                  <Grid container>
                                    <Grid item xs={12}>
                                      <InputLabel
                                        className="field-label"
                                        sx={fieldLabelSx}
                                      >
                                        Intervention Type*
                                      </InputLabel>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      className="field-container"
                                    >
                                      <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={viewOnly}
                                      >
                                        <Select
                                          displayEmpty
                                          value={interventionType}
                                          onChange={(e) =>
                                            setInterventionType(
                                              e.target.value as string,
                                            )
                                          }
                                          input={selectInput}
                                          renderValue={(v) =>
                                            v
                                              ? (v as string)
                                              : emptySelectPlaceholder(
                                                  "Select Type",
                                                )
                                          }
                                        >
                                          <MenuItem value="">
                                            <em>Select Type</em>
                                          </MenuItem>
                                          {interventionTypes.map((t) => (
                                            <MenuItem
                                              key={t.value}
                                              value={t.value}
                                            >
                                              {t.value}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  </Grid>

                                  <Grid container>
                                    <Grid item xs={12}>
                                      <InputLabel
                                        className="field-label"
                                        sx={fieldLabelSx}
                                      >
                                        Activity*
                                      </InputLabel>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      className="field-container"
                                    >
                                      <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={isLockedForEditing}
                                        required
                                      >
                                        <Select
                                          displayEmpty
                                          required
                                          value={activity}
                                          onChange={(e) =>
                                            setActivity(
                                              e.target.value as string,
                                            )
                                          }
                                          input={selectInput}
                                          renderValue={(v) =>
                                            v
                                              ? (v as string)
                                              : emptySelectPlaceholder(
                                                  "Select Activity",
                                                )
                                          }
                                        >
                                          <MenuItem value="">
                                            <em>Select Activity</em>
                                          </MenuItem>
                                          {activity &&
                                            !activityOptions.some(
                                              (a) => a.value === activity,
                                            ) && (
                                              // Preserves a legacy activity value (e.g. old Care Gap Closure
                                              // records saved with "Describe in Note") so the field is not
                                              // blank and the user is not blocked from saving without changes.
                                              // Selecting a real option below replaces it permanently.
                                              <MenuItem value={activity}>
                                                {activity}
                                              </MenuItem>
                                            )}
                                          {activityOptions.map((a) => (
                                            <MenuItem
                                              key={a.value}
                                              value={a.value}
                                            >
                                              {a.value}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  </Grid>

                                  {isVaccinationType ? (
                                    <Grid container>
                                      <Grid item xs={12}>
                                        <InputLabel
                                          className="field-label"
                                          sx={fieldLabelSx}
                                        >
                                          Vaccine*
                                        </InputLabel>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        className="field-container"
                                      >
                                        <FormControl
                                          fullWidth
                                          size="small"
                                          disabled={viewOnly}
                                        >
                                          <Select
                                            displayEmpty
                                            value={vaccineName}
                                            onChange={(e) =>
                                              setVaccineName(
                                                e.target.value as string,
                                              )
                                            }
                                            input={selectInput}
                                            renderValue={(v) =>
                                              v
                                                ? (v as string)
                                                : emptySelectPlaceholder(
                                                    "Select Vaccine",
                                                  )
                                            }
                                          >
                                            <MenuItem value="">
                                              <em>Select Vaccine</em>
                                            </MenuItem>
                                            {VACCINE_OPTIONS.map((v) => (
                                              <MenuItem key={v} value={v}>
                                                {v}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                    </Grid>
                                  ) : (
                                    <Grid container>
                                      <Grid item xs={12}>
                                        <InputLabel
                                          className="field-label"
                                          sx={fieldLabelSx}
                                        >
                                          Related Condition/Dx
                                        </InputLabel>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        className="field-container"
                                      >
                                        <Autocomplete<
                                          DiagnosisOption,
                                          false,
                                          false,
                                          false
                                        >
                                          options={displayDxOptions}
                                          value={relatedConditionDx}
                                          inputValue={relatedDxInputValue}
                                          filterOptions={(opts) => opts}
                                          sx={{
                                            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall":
                                              {
                                                paddingTop: "0px",
                                                paddingBottom: "0px",
                                              },
                                          }}
                                          isOptionEqualToValue={(a, b) =>
                                            !!a && !!b && a[0] === b[0]
                                          }
                                          onInputChange={(_, newInput, reason) => {
                                            setRelatedDxInputValue(newInput);
                                            if (reason === "clear") {
                                              setRelatedConditionDx(null);
                                              setNlmDxOptions([]);
                                              if (nlmDxDebounceRef.current)
                                                clearTimeout(
                                                  nlmDxDebounceRef.current,
                                                );
                                              return;
                                            }
                                            if (reason === "input") {
                                              fetchNlmDiagnosisForInput(newInput);
                                              if (relatedConditionDx) {
                                                const label = `${relatedConditionDx[0]} — ${relatedConditionDx[1]}`;
                                                if (newInput !== label) {
                                                  setRelatedConditionDx(null);
                                                }
                                              }
                                            }
                                          }}
                                          onChange={(_, newValue) => {
                                            setRelatedConditionDx(newValue);
                                            if (!newValue) {
                                              setRelatedDxInputValue("");
                                              setNlmDxOptions([]);
                                              if (nlmDxDebounceRef.current)
                                                clearTimeout(
                                                  nlmDxDebounceRef.current,
                                                );
                                            }
                                          }}
                                          getOptionLabel={(option) =>
                                            option
                                              ? `${option[0]} — ${option[1]}`
                                              : ""
                                          }
                                          slotProps={{
                                            popper: {
                                              sx: { zIndex: 3000 },
                                              placement: "bottom-start",
                                            },
                                            paper: {
                                              sx: {
                                                boxShadow:
                                                  "0 4px 20px rgba(0,0,0,0.15)",
                                              },
                                            },
                                          }}
                                          ListboxProps={{
                                            style: { maxHeight: 280 },
                                          }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              size="small"
                                              className="the-field"
                                              placeholder="Enter Condition/diagnosis"
                                              disabled={viewOnly}
                                              sx={placeholderSx}
                                            />
                                          )}
                                          renderOption={(props, option) => (
                                            <li
                                              {...props}
                                              key={`${option[0]}-${option[1]}`}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  alignItems: "flex-start",
                                                  width: "100%",
                                                  lineHeight: "18px",
                                                  py: 0.25,
                                                }}
                                              >
                                                <Box
                                                  component="span"
                                                  sx={{
                                                    fontWeight: 700,
                                                    color: "#2F2B2A",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {option[0]}
                                                </Box>
                                                <Box
                                                  component="span"
                                                  sx={{
                                                    color: "#4A4442",
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-word",
                                                  }}
                                                >
                                                  {option[1]}
                                                </Box>
                                              </Box>
                                            </li>
                                          )}
                                          disabled={viewOnly}
                                        />
                                      </Grid>
                                    </Grid>
                                  )}

                                  <Box
                                    className="divider"
                                    sx={{
                                      background: "#D9D9D9",
                                      height: "1px",
                                      width: "97% !important",
                                      marginTop: "15px !important",
                                      marginRight: "0 !important",
                                      marginBottom: "0 !important",
                                      marginLeft: "-5px !important",
                                    }}
                                  />

                                  <Box
                                    sx={{
                                      color: "#989898",
                                      fontFamily: "QuicksandMedium",
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      lineHeight: "18px",
                                      marginTop: "15px",
                                      marginBottom: "6px",
                                    }}
                                  >
                                    Select Medication or Device Info (Optional)
                                  </Box>
                                  <Box
                                    sx={{
                                      color: "#989898",
                                      fontFamily: "QuicksandMedium",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      lineHeight: "18px",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    An intervention may relate to a medication
                                    and/or device. Select one, both or neither
                                    as desired.
                                  </Box>

                                  <Grid container>
                                    <Grid item xs={12}>
                                      <InputLabel
                                        className="field-label"
                                        sx={{
                                          ...fieldLabelSx,
                                          marginTop: "6px",
                                        }}
                                      >
                                        Medication Involved
                                      </InputLabel>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      className="field-container"
                                    >
                                      <TextField
                                        size="small"
                                        value={medicationInvolved}
                                        className="the-field"
                                        placeholder="Select Medication"
                                        disabled={viewOnly}
                                        onChange={(e) =>
                                          setMedicationInvolved(e.target.value)
                                        }
                                        sx={placeholderSx}
                                      />
                                      {medicationSearch &&
                                      medicationSearch.length > 0 ? (
                                        <>
                                          <span
                                          >
                                            Available Medications{" "}
                                            <small>
                                              (Click medication below to
                                              populate field)
                                            </small>
                                          </span>
                                          <div
                                            style={{
                                              border: "1px solid #929292",
                                              borderRadius: 6,
                                              maxHeight: 200,
                                              overflow: "auto",
                                              padding: 10,
                                              width: "95%",
                                              boxSizing: "border-box",
                                            }}
                                          >
                                            {medicationSearch.map(
                                              (
                                                medication: string,
                                                index: number,
                                              ) => (
                                                <div
                                                  key={index}
                                                  className={
                                                    index % 2 === 0
                                                      ? "rowEven"
                                                      : "row"
                                                  }
                                                  style={{
                                                    cursor: "pointer",
                                                    padding: 6,
                                                    fontFamily:
                                                      "QuicksandMedium",
                                                  }}
                                                  onClick={() => {
                                                    setMedicationInvolved(
                                                      medication,
                                                    );
                                                    setMedicationSearch(null);
                                                  }}
                                                >
                                                  {medication}
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        </>
                                      ) : (
                                        <span
                                          style={{
                                            fontFamily: "QuicksandMedium",
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "#868382",
                                          }}
                                        >
                                          Search for available medications.
                                        </span>
                                      )}
                                    </Grid>
                                  </Grid>

                                  <Grid container>
                                    <Grid item xs={12}>
                                      <InputLabel
                                        className="field-label"
                                        sx={fieldLabelSx}
                                      >
                                        Device Involved
                                      </InputLabel>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      className="field-container"
                                    >
                                      <TextField
                                        size="small"
                                        value={deviceInvolved}
                                        className="the-field"
                                        placeholder="Enter Device"
                                        disabled={viewOnly}
                                        onChange={(e) =>
                                          setDeviceInvolved(e.target.value)
                                        }
                                        sx={placeholderSx}
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>

                            {/* Notes */}
                            <Box
                              sx={{
                                flex: "1 1 390px",
                                minWidth: 0,
                                px: { lg: 3 },
                                borderRight: { lg: "1px solid #D9D9D9" },
                                "@media (max-width: 1200px)": {
                                  borderRight: "none",
                                  borderBottom: "1px solid #D9D9D9",
                                  px: 0,
                                  pb: 3,
                                  mb: 2,
                                },
                              }}
                            >
                              <Grid container className="form-container">
                                <Grid
                                  item
                                  xs={12}
                                  className="patient-info-left-container form-left-container"
                                  style={{ marginLeft: 0 }}
                                >
                                  <Grid container>
                                    <Grid item xs={12}>
                                      <InputLabel
                                        className="field-label"
                                        sx={fieldLabelSx}
                                      >
                                        Notes
                                      </InputLabel>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      className="field-container"
                                      style={{ marginLeft: 0 }}
                                    >
                                      <TextField
                                        size="small"
                                        value={notes}
                                        className="the-field"
                                        placeholder="Description (optional)"
                                        disabled={viewOnly}
                                        multiline
                                        variant="outlined"
                                        minRows={12}
                                        onChange={(e) =>
                                          setNotes(e.target.value)
                                        }
                                        sx={{
                                          width: "100% !important",
                                          maxWidth: "100%",
                                          "& .MuiOutlinedInput-root": {
                                            padding: "0px !important",
                                            width: "100%",
                                          },
                                          "& .MuiInputBase-inputMultiline": {
                                            padding: "8px 16px !important",
                                            boxSizing: "border-box",
                                          },
                                          ...placeholderSx,
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>

                            {/* Workflow card — stretches so bottom aligns with Notes area */}
                            <Box
                              sx={{
                                flexShrink: 0,
                                width: "340px",
                                minHeight: "390px",
                                paddingLeft: { lg: 2 },
                                background: "#FFF",
                                boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
                                padding: "44px 16px",
                                boxSizing: "border-box",
                                alignSelf: "stretch",
                              }}
                            >
                              <WorkflowCheckboxRow
                                checked={needsSupervisorReview}
                                onChange={setNeedsSupervisorReview}
                                disabled={viewOnly}
                                title="Needs Supervisor Review"
                                description="Indicates the pended state of this intervention requires peer review and authorized signature approval."
                              />
                              <WorkflowCheckboxRow
                                checked={receivedPrescriberResponse}
                                onChange={setReceivedPrescriberResponse}
                                disabled={viewOnly}
                                title="Received prescriber response"
                                description="Use this field to document the prescriber's response once it has been received."
                              />
                              {receivedPrescriberResponse && (
                                <Box sx={{ mb: 2, pl: 4, pr: 0 }}>
                                  <InputLabel
                                    className="field-label"
                                    sx={{
                                      ...fieldLabelSx,
                                      marginBottom: "6px",
                                    }}
                                  >
                                    Response
                                  </InputLabel>
                                  <Box
                                    className="field-container"
                                    sx={{
                                      marginLeft: "0 !important",
                                      width: "100%",
                                    }}
                                  >
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      disabled={viewOnly}
                                      sx={{
                                        "& .MuiInputBase-root": {
                                          backgroundColor: "#efe9e7",
                                          height: "41.13px",
                                        },
                                        "& .MuiInputBase-root.Mui-disabled": {
                                          backgroundColor: "#F5F5F5",
                                        },
                                        "& .MuiSelect-select": {
                                          height: "41.13px",
                                          boxSizing: "border-box",
                                          display: "flex",
                                          alignItems: "center",
                                        },
                                      }}
                                    >
                                      <Select
                                        displayEmpty
                                        value={prescriberResponseValue}
                                        onChange={(e) =>
                                          setPrescriberResponseValue(
                                            e.target.value as string,
                                          )
                                        }
                                        input={selectInput}
                                        renderValue={(v) =>
                                          v
                                            ? (v as string)
                                            : emptySelectPlaceholder(
                                                "Select response",
                                              )
                                        }
                                      >
                                        {PRESCRIBER_RESPONSE_OPTIONS.map(
                                          (opt) => (
                                            <MenuItem key={opt} value={opt}>
                                              {opt}
                                            </MenuItem>
                                          ),
                                        )}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Box>
                              )}
                              <WorkflowCheckboxRow
                                checked={annualizedHardSavings}
                                onChange={setAnnualizedHardSavings}
                                disabled={viewOnly}
                                title="Annualized Hard Savings"
                                description="Enter the hard savings amount here if the intervention resulted in measurable financial savings."
                              />
                              {annualizedHardSavings && (
                                <Box sx={{ pl: 4, pr: 0 }}>
                                  <InputLabel
                                    className="field-label"
                                    sx={{
                                      ...fieldLabelSx,
                                      marginBottom: "6px",
                                    }}
                                  >
                                    Savings amount
                                  </InputLabel>
                                  <Box
                                    className="field-container"
                                    sx={{
                                      marginLeft: "0 !important",
                                      width: "100%",
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      type="text"
                                      inputMode="decimal"
                                      value={savingsAmount}
                                      onChange={handleSavingsChange}
                                      onBlur={handleSavingsBlur}
                                      disabled={viewOnly}
                                      placeholder="0.00"
                                      className="the-field"
                                      inputProps={{
                                        inputMode: "decimal",
                                        pattern: "[0-9]*\\.?[0-9]{0,2}",
                                        maxLength: 14,
                                      }}
                                      sx={{
                                        ...placeholderSx,
                                        "& .MuiOutlinedInput-root": {
                                          backgroundColor: "#efe9e7",
                                        },
                                        "& .MuiOutlinedInput-root.Mui-disabled":
                                          {
                                            backgroundColor: "#F5F5F5",
                                          },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InterventionInformationScreen;
